/**
 * Lists: a map read as an outline.
 *
 * The other families spread a map out; a list lines it up. Everything under a branch becomes an
 * indented list, one item per row, in the order it was written — which is how a map about a plan,
 * a table of contents or a checklist is actually read.
 *
 * Six directions, in two kinds:
 *
 * - **Down and up** are one outline for the whole map: every node on its own row below (or above)
 *   the root, indented by its depth. Up-down splits the root's branches, half below and half above.
 * - **Right and left** make the root's branches into *columns* hanging off a bus that runs
 *   sideways from the root, each column an outline of its own branch. Left-right puts columns on
 *   both sides.
 *
 * The renderer draws the connectors (`listRoute` in the scene); this places the boxes. Like every
 * layout here: the root does not move, a collapsed branch takes no room, a pinned node keeps its
 * place, and a second run over an arranged map reports nothing.
 */
import { childrenOf } from '@orbit-mind/domain';
import { parseLayoutId } from '@orbit-mind/shared-types';
import { onlyMoves } from './moves';
import { spacingFrom, } from './types';
/** How much further in each level of an outline sits. Shared with the renderer's connectors. */
export const LIST_INDENT = 28;
/** Between two rows of an outline. */
const ROW_GAP = 10;
export const LIST_FAMILIES = ['cornered-list', 'simple-list-cornered', 'continuous-list'];
export function packList(input, layoutId) {
    const { document } = input;
    const root = document.nodes[document.map.rootNodeId];
    const { direction } = parseLayoutId(layoutId);
    if (root === undefined || direction === null)
        return { positions: {} };
    const spacing = spacingFrom(input.spacing);
    const size = (node) => input.extentOf(node.id);
    const pinned = (node) => node.pinned === true && input.preserveManualPositions;
    const children = (node) => node.collapsed ? [] : childrenOf(document, node.id).filter((child) => !child.hidden);
    /** A node's descendants, depth first, as outline rows. */
    const rowsUnder = (head) => {
        const rows = [];
        const walk = (node, depth) => {
            for (const child of children(node)) {
                rows.push({ node: child, depth });
                walk(child, depth + 1);
            }
        };
        walk(head, 1);
        return rows;
    };
    const placements = {};
    const place = (node, x, y) => {
        if (!pinned(node))
            placements[node.id] = { x, y };
    };
    /**
     * Stacks rows below (`+1`) or above (`-1`) a head, indented from `left`. Returns where the
     * stack ended, so the next stack can start beyond it.
     */
    const stack = (rows, left, from, sign) => {
        let cursor = from;
        for (const row of rows) {
            const height = size(row.node).height;
            const y = sign === 1 ? cursor : cursor - height;
            place(row.node, left + LIST_INDENT * row.depth, y);
            cursor = sign === 1 ? cursor + height + ROW_GAP : cursor - height - ROW_GAP;
        }
        return cursor;
    };
    const rootSize = size(root);
    const branches = children(root);
    if (direction === 'down' || direction === 'up' || direction === 'up-down') {
        // One outline of the whole map. Up-down alternates branches below and above.
        let below = root.y + rootSize.height + ROW_GAP;
        let above = root.y - ROW_GAP;
        branches.forEach((branch, index) => {
            const goesUp = direction === 'up' || (direction === 'up-down' && index % 2 === 1);
            const rows = [
                { node: branch, depth: 1 },
                ...rowsUnder(branch).map((row) => ({ node: row.node, depth: row.depth + 1 })),
            ];
            if (goesUp)
                above = stack(rows, root.x, above, -1);
            else
                below = stack(rows, root.x, below, 1);
        });
        return { positions: onlyMoves(document, placements) };
    }
    // Columns off a bus. Each column is a branch head and its outline below it.
    const headsTop = root.y + rootSize.height + spacing.rank / 2;
    const widthOf = (branch) => Math.max(size(branch).width, ...rowsUnder(branch).map((row) => LIST_INDENT * row.depth + size(row.node).width));
    let rightCursor = root.x + rootSize.width + spacing.subtree;
    let leftCursor = root.x - spacing.subtree;
    branches.forEach((branch, index) => {
        const goesLeft = direction === 'left' || (direction === 'left-right' && index % 2 === 1);
        const width = widthOf(branch);
        const left = goesLeft ? leftCursor - width : rightCursor;
        place(branch, left, headsTop);
        stack(rowsUnder(branch), left, headsTop + size(branch).height + ROW_GAP, 1);
        if (goesLeft)
            leftCursor = left - spacing.subtree;
        else
            rightCursor = left + width + spacing.subtree;
    });
    return { positions: onlyMoves(document, placements) };
}
export function listLayout(layoutId, name) {
    return { id: layoutId, name, run: (input) => packList(input, layoutId) };
}
//# sourceMappingURL=list.js.map